/**
 * Statement review upload — /statement-review. The visitor uploads one or
 * more merchant statements; the engine stages them in S3 and the backend
 * attaches them to the Zoho Lead.
 */
import { useRef } from "react";
import analytics from "@/utils/analytics";
import { Field } from "@/forms/engine/fields";
import { companyRequired, currentProvider, email, fullName, phoneRequired } from "./common";

export const STATEMENT_ITEM_NAME = "statement_review_upload";
const STATEMENT_ITEM_OPTS = { itemType: "upload" };

/** Turnover band slugs kept from the previous form; the CRM stores a
 *  currency, so the band's midpoint goes to Monthly_Turnover. */
const TURNOVER_OPTIONS = [
  { value: "under_10k", label: "Under £10,000", midpoint: 5000 },
  { value: "band_10_30", label: "£10,000–£30,000", midpoint: 20000 },
  { value: "band_30_75", label: "£30,000–£75,000", midpoint: 50000 },
  { value: "over_75", label: "£75,000+", midpoint: 100000 },
];

const BUSINESS_TYPE_OPTIONS = [
  { value: "retail", label: "Retail" },
  { value: "hospitality", label: "Hospitality / restaurant / bar" },
  { value: "service", label: "Service business" },
  { value: "ecommerce", label: "E-commerce / online store" },
  { value: "multi_site", label: "Multi-site business" },
  { value: "other", label: "Other" },
];

/** Payment channel → How_do_you_take_payments_now (multiselect). */
const CHANNEL_CRM = {
  in_person: ["Card machine"],
  online: ["Online payments"],
  both: ["Card machine", "Online payments"],
};

const CHANNEL_OPTIONS = [
  { value: "in_person", label: "In person" },
  { value: "online", label: "Online" },
  { value: "both", label: "Both" },
];

const REVIEW_GOALS = [
  { value: "overpaying", label: "Check if I'm overpaying" },
  { value: "full_review", label: "Full statement review" },
  { value: "quote_compare", label: "Quote comparison" },
  { value: "fee_help", label: "Help understanding my current fees" },
];

const filesField = {
  name: "statement_files", type: "file", label: "Statement upload", required: true, maxFiles: 5,
  requiredMessage: "Please upload at least one statement file",
  help: "Upload a recent merchant statement or fee summary. PDF preferred.",
  placeholder: "PDF, DOCX, JPG, PNG up to 10MB each (max 5 files)",
  answerLabel: "Statement files",
};

/** The engine's file field, plus a file-selection event the first time a file
 * is added. Selection is not upload, transfer, CRM delivery or lead success. */
export function StatementFiles(props) {
  const firedRef = useRef(false);
  const onChange = (files) => {
    if (!firedRef.current && files.length > (props.value || []).length) {
      // A refused selection remains a functional form interaction only. It
      // must not consume the marker that permits a later, newly selected
      // file to be measured after consent is granted.
      if (analytics.statementFileSelected(STATEMENT_ITEM_NAME, { extra: { file_count: files.length } })) {
        firedRef.current = true;
      }
    }
    props.onChange(files);
  };
  return <Field {...props} field={filesField} onChange={onChange} />;
}

const goalLabels = (values) =>
  (values.review_goals || []).map((v) => REVIEW_GOALS.find((g) => g.value === v)?.label).filter(Boolean);

const statementReview = {
  id: "statement_review",
  label: "Statement review",
  crm: { layout: "Standard", leadSource: "Website Statement Review", tags: [] },
  steps: [
    {
      title: "Your statement",
      fields: [
        { ...filesField, render: (props) => <StatementFiles {...props} /> },
        [
          {
            name: "monthly_turnover_band", type: "select", label: "Monthly card turnover", required: true,
            requiredMessage: "Please select your monthly card turnover", placeholder: "Select...",
            options: TURNOVER_OPTIONS.map(({ value, label }) => ({ value, label })),
            crm: (value) => {
              const band = TURNOVER_OPTIONS.find((b) => b.value === value);
              return band ? { Monthly_Turnover: band.midpoint } : {};
            },
          },
          {
            name: "business_type", type: "select", label: "Business type", required: true,
            requiredMessage: "Please select your business type", placeholder: "Select...",
            options: BUSINESS_TYPE_OPTIONS, crm: "IndustrySector",
          },
        ],
        {
          name: "payment_channel", type: "radio", label: "Payment channel", required: true,
          requiredMessage: "Please select how you take payments", options: CHANNEL_OPTIONS,
          crm: (value) => ({ How_do_you_take_payments_now: CHANNEL_CRM[value] || [] }),
        },
        {
          name: "review_goals", type: "chips", label: "What would you like us to review?", required: true,
          requiredMessage: "Please select at least one option", options: REVIEW_GOALS,
        },
      ],
    },
    {
      title: "Your details",
      fields: [
        [
          { ...fullName, label: "Full name", placeholder: "Your full name" },
          { ...companyRequired, placeholder: "Your business" },
        ],
        [
          { ...email, label: "Email address", placeholder: "you@example.com" },
          {
            ...phoneRequired, label: "Phone number", placeholder: "07xxx xxxxxx",
            help: "We'll use this to talk through your statement findings.",
          },
        ],
        currentProvider,
        {
          name: "notes", type: "textarea", label: "Optional notes", crm: "Description", maxLength: 1000, rows: 3,
          placeholder: "Anything specific you would like us to look at?",
        },
        {
          name: "file_consent", type: "checkbox", answerLabel: "Consent to statement review",
          label: "I confirm that I am happy for this information to be reviewed for the purpose of receiving a tailored payment review.",
          validate: (value) => (value ? null : "Please confirm you're happy for us to review this information"),
        },
      ],
    },
  ],
  submitLabel: "Submit for review",
  consent: { variant: "standard" },
  footer: (
    <p className="text-[11px] text-brand-grey leading-relaxed text-center">
      Your files and information will only be used for the purpose of reviewing your payment setup and responding to your request.
    </p>
  ),
  success: {
    title: "Thanks — your statement review request has been received",
    body: "We've received your files and details. A member of the team will review your statement and come back to you with the most relevant next step.",
  },
  summary: (values) => {
    const goals = goalLabels(values);
    return goals.length ? `Statement review: ${goals.join(", ")}` : "Statement review";
  },
  // Tool lifecycle + upload funnel events kept from the previous form.
  events: {
    start: () => analytics.toolStarted(STATEMENT_ITEM_NAME, STATEMENT_ITEM_OPTS),
    success: () => {
      analytics.toolCompleted(STATEMENT_ITEM_NAME, STATEMENT_ITEM_OPTS);
      analytics.leadSubmitted(STATEMENT_ITEM_NAME, {
        itemCategory: "upload", itemType: "upload", submissionMethod: "upload",
      });
    },
  },
};

export default statementReview;
